import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Lock, CreditCard, FileText, Zap, MapPin, CheckCircle2, Star } from 'lucide-react';
import { Countdown } from './Countdown';
function TopBar() {
  return <div className="sticky top-0 z-50 bg-charcoal text-white py-2 px-4 shadow-md" data-id="element-72">
      <div className="max-w-5xl mx-auto flex items-center justify-between text-sm" data-id="element-73">
        <div className="flex items-center gap-2 font-bold tracking-wide" data-id="element-74">
          <span data-id="element-75">🇦🇷</span> AIRPACK™
        </div>
        <div className="flex items-center gap-3" data-id="element-76">
          <div className="hidden sm:flex items-center gap-2 text-gray-300" data-id="element-77">
            <span data-id="element-78">Termina en:</span>
            <Countdown compact data-id="element-79" />
          </div>
          <button onClick={() => document.getElementById('pricing')?.scrollIntoView({
          behavior: 'smooth'
        })} className="bg-ember hover:bg-ember-dark text-white px-3 py-1 rounded text-xs font-bold transition-colors" data-id="element-80">
            VER OFERTA
          </button>
        </div>
      </div>
    </div>;
}
function TestimonialStrip() {
  const testimonials = [{
    initials: 'MG',
    text: 'Organicé toda la semana en una tarde. Dejé de pedir delivery.',
    author: 'Marcela G.',
    city: 'Córdoba'
  }, {
    initials: 'LP',
    text: 'Las milanesas por fin me salen crocantes. Vale cada peso.',
    author: 'Laura P.',
    city: 'Rosario'
  }, {
    initials: 'RM',
    text: 'Hice un asado completo en el departamento. Mi señora no lo podía creer.',
    author: 'Roberto M.',
    city: 'Buenos Aires'
  }];
  return <div className="w-full bg-white border-y border-sand py-8 mt-16" data-id="element-81">
      <div className="max-w-6xl mx-auto px-4" data-id="element-82">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-83">
          {testimonials.map((t, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className="flex flex-col gap-3 p-4 rounded-xl bg-cream/50 border border-sand/50" data-id="element-84">
              <div className="flex items-center gap-3" data-id="element-85">
                <div className="w-10 h-10 rounded-full bg-ember/10 text-ember flex items-center justify-center font-bold text-sm" data-id="element-86">
                  {t.initials}
                </div>
                <div className="flex text-amber-400" data-id="element-87">
                  {[...Array(5)].map((_, j) => <Star key={j} size={14} fill="currentColor" data-id="element-88" />)}
                </div>
              </div>
              <p className="text-charcoal/80 text-sm font-medium italic" data-id="element-89">
                "{t.text}"
              </p>
              <p className="text-xs text-charcoal/60 font-semibold" data-id="element-90">
                — {t.author}, {t.city}
              </p>
            </motion.div>)}
        </div>
      </div>
    </div>;
}
export function Hero() {
  return <>
      <TopBar data-id="element-91" />
      <section className="pt-8 md:pt-12 pb-0 relative overflow-hidden" data-id="element-92">
        {/* Subtle background texture/blob */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-[500px] bg-ember/5 rounded-full blur-3xl -z-10" data-id="element-93" />

        <div className="max-w-3xl mx-auto px-4 text-center flex flex-col items-center" data-id="element-94">
          {/* Compact above-the-fold, single centered column */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-sand text-xs font-bold tracking-widest uppercase text-charcoal/70 mb-4 shadow-sm" data-id="element-95">
            <span data-id="element-96">🇦🇷</span> AIRPACK™
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-3xl md:text-5xl font-serif font-bold text-charcoal leading-[1.05] tracking-tight mb-4" data-id="element-97">
            La forma más simple de dejar de depender del delivery.
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="text-base md:text-lg text-charcoal/80 mb-5 leading-relaxed max-w-2xl" data-id="element-98">
            Resolvé almuerzos, cenas, viandas, picadas y comidas familiares
            durante todo el año usando tu Air Fryer. Sin improvisar. Sin volver
            a preguntarte qué cocinar.
          </motion.p>

          {/* Benefit chips — compact */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} className="flex flex-wrap justify-center gap-2 mb-4" data-id="element-99">
            {['365 días de comidas resueltas', 'Más de 1000 recetas', 'Ingredientes argentinos reales', 'Acceso inmediato'].map((text, i) => <div key={i} className="flex items-center gap-1.5 bg-white px-2.5 py-1 rounded-md border border-sand text-[11px] font-semibold text-charcoal/80 shadow-sm" data-id="element-100">
                <CheckCircle2 size={13} className="text-green-600" data-id="element-101" />
                {text}
              </div>)}
          </motion.div>

          {/* Product mockup — between subtitle/chips and CTA */}
          <motion.div initial={{
          opacity: 0,
          y: 30,
          scale: 0.96
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} transition={{
          delay: 0.35,
          duration: 0.6,
          ease: 'easeOut'
        }} className="relative flex justify-center mb-5" data-id="element-102">
            <motion.img src="https://cdn.magicpatterns.com/uploads/aDhVoP1Qn83NtjntK2uTvX/ChatGPT_Image_12_jun_2026,_09_35_48_a.m..png" alt="AirPack — 1000 Recetas Air Fryer con 6 tomos digitales y 6 bonos" className="h-auto w-auto max-h-[230px] sm:max-h-[280px] md:max-h-[320px] object-contain" animate={{
            y: [0, -8, 0]
          }} transition={{
            duration: 5,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-103" />
          </motion.div>

          {/* CTA */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.4
        }} className="flex flex-col items-center gap-3" data-id="element-104">
            <button type="button" onClick={() => document.getElementById('pricing')?.scrollIntoView({
            behavior: 'smooth'
          })} className="group relative inline-flex items-center justify-center gap-3 bg-ember hover:bg-ember-dark text-white px-7 py-4 rounded-xl text-base md:text-lg font-bold transition-all hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl w-full sm:w-auto" data-id="element-105">
              <span className="text-xl group-hover:translate-x-1 transition-transform" data-id="element-106">
                👉
              </span>
              QUIERO DEJAR DE DEPENDER DEL DELIVERY
            </button>

            <div className="flex flex-wrap justify-center gap-4 text-xs font-medium text-charcoal/60" data-id="element-107">
              <span className="flex items-center gap-1" data-id="element-108">
                <ShieldCheck size={14} data-id="element-109" /> Garantía 7 días
              </span>
              <span className="flex items-center gap-1" data-id="element-110">
                <Lock size={14} data-id="element-111" /> Pago seguro
              </span>
              <span className="flex items-center gap-1" data-id="element-112">
                <CreditCard size={14} data-id="element-113" /> Mercado Pago
              </span>
            </div>
          </motion.div>
        </div>

        <div className="max-w-3xl mx-auto px-4" data-id="element-114">
          {/* spacer wrapper to keep stat strip alignment */}

          {/* Stat strip */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.7
        }} className="mt-10 pt-6 border-t border-sand/60 flex flex-wrap justify-center gap-x-8 gap-y-3 text-sm font-bold text-charcoal/80" data-id="element-115">
            <span data-id="element-116">+1.200 hogares</span>
            <span className="hidden sm:inline text-sand" data-id="element-117">•</span>
            <span data-id="element-118">6 Tomos</span>
            <span className="hidden sm:inline text-sand" data-id="element-119">•</span>
            <span data-id="element-120">4 Bonos</span>
            <span className="hidden sm:inline text-sand" data-id="element-121">•</span>
            <span className="flex items-center gap-1" data-id="element-122">
              4.9/5 <Star size={14} className="text-amber-400 fill-current" data-id="element-123" />
            </span>
          </motion.div>
        </div>

        <TestimonialStrip data-id="element-124" />
      </section>
    </>;
}